import { round } from 'src/helpers/units_helpers';

export type BLESettingType = 'offset' | 'gain';
export interface BLESetting {
	title: string;
	characteristicUUID: string;
	type: BLESettingType;
	command: string;
	parameter: string;
	success: string;
	error: string;
	factor: number;
	rawMin: number;
	rawMax: number;
	min: number;
	max: number;
	step: number;
	unit: string;
	default_value: number;
	component: string;
	formula: (value: number, factor?: number) => number;
	applyformula: (value: number, factor?: number) => number;
}
export interface BLESettingsConfigMap {
	temperature: BLESetting;
	humidity: BLESetting;
	pressure: BLESetting;
	wind_direction: BLESetting;
	illuminance: BLESetting;
	uv_index: BLESetting;
	wind_speed: BLESetting;
}

export const BLESettingsConfig: BLESettingsConfigMap = {
	temperature: {
		title: 'sub_settings_title_temperature_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a000',
		type: 'offset',
		command: 'setTemperatureOffset',
		parameter: 'temperatureOffset',
		success: 'temperatureOffsetSet',
		error: 'failedToSetTemperatureOffset',
		factor: 0.1,
		rawMin: -32768,
		rawMax: 32767,
		min: -50,
		max: 50,
		step: 0.1,
		unit: '°C',
		default_value: 0,
		component: 'temperature:0',
		//value + offset * mult
		formula: (rawValue, factor) => round(rawValue * factor, 1),
		applyformula: (val, factor) => Math.round(val / factor),
	},
	humidity: {
		title: 'sub_settings_title_humidity_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a002',
		type: 'offset',
		command: 'setHumidityOffset',
		parameter: 'humidityOffset',
		success: 'humidityOffsetSet',
		error: 'failedToSetHumidityOffset',
		factor: 1,
		rawMin: -32768,
		rawMax: 32767,
		min: -30,
		max: 30,
		step: 1,
		unit: '%',
		default_value: 0,
		component: 'humidity:0',
		//value + offset * mult
		formula: (rawValue, factor) => Math.round(rawValue * factor),
		applyformula: (val, factor) => Math.round(val / factor),
	},
	pressure: {
		title: 'sub_settings_title_pressure_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a004',
		type: 'offset',
		command: 'setPressureOffset',
		parameter: 'pressureOffset',
		success: 'pressureOffsetSet',
		error: 'failedToSetPressureOffset',
		factor: 0.1,
		rawMin: -32768,
		rawMax: 32767,
		min: -100,
		max: 100,
		step: 0.1,
		unit: 'hPa',
		default_value: 0,
		component: 'pressure:0',
		//value + offset * mult
		formula: (rawValue, factor) => round(rawValue * factor, 1),
		applyformula: (val, factor) => Math.round(val / factor),
	},
	wind_direction: {
		title: 'sub_settings_title_wind_direction_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a006',
		type: 'offset',
		command: 'setWindDirectionOffset',
		parameter: 'windDirectionOffset',
		success: 'windDirectionOffsetSet',
		error: 'failedToSetWindDirectionOffset',
		factor: 1,
		rawMin: -32768,
		rawMax: 32767,
		min: -180,
		max: 180,
		step: 1,
		unit: '°',
		default_value: 0,
		component: 'direction:0',
		//(value + offset * mult) modulo 360
		formula: (rawValue) => Math.round(rawValue) % 360,
		applyformula: (val) => Math.round(val) % 360,
	},
	illuminance: {
		title: 'sub_settings_title_illuminance_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a008',
		type: 'gain',
		command: 'setIlluminanceGain',
		parameter: 'illuminanceGain',
		success: 'illuminanceGainSet',
		error: 'failedToSetIlluminanceGain',
		factor: 1,
		rawMin: -10000,
		rawMax: 10000,
		min: -100,
		max: 100,
		step: 1,
		unit: '%',
		default_value: 1,
		component: 'illuminance:0',
		//value * (1 + gain / 10000)
		formula: (rawValue) => Math.round(rawValue / 100),
		applyformula: (val) => Math.round(val * 100),
	},
	uv_index: {
		title: 'sub_settings_title_uv_index_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a00a',
		type: 'gain',
		command: 'setUVIndexGain',
		parameter: 'UVIndexGain',
		success: 'UVIndexGainSet',
		error: 'failedToSetUVIndexGain',
		factor: 1,
		rawMin: -10000,
		rawMax: 10000,
		min: -100,
		max: 100,
		step: 1,
		unit: '%',
		default_value: 1,
		component: 'UV:0',
		//value * (1 + gain / 10000)
		formula: (rawValue) => Math.round(rawValue / 100),
		applyformula: (val) => Math.round(val * 100),
	},
	wind_speed: {
		title: 'sub_settings_title_wind_speed_offset',
		characteristicUUID: '0de178e5-a95d-4988-b042-7145d540a00c',
		type: 'gain',
		command: 'setWindSpeedGain',
		parameter: 'windSpeedGain',
		success: 'windSpeedGainSet',
		error: 'failedToSetWindSpeedGain',
		factor: 1,
		rawMin: -10000,
		rawMax: 10000,
		min: -100,
		max: 100,
		step: 1,
		unit: '%',
		default_value: 1,
		component: 'speed:0',
		//value * (1 + gain / 10000)
		formula: (rawValue) => Math.round(rawValue / 100),
		applyformula: (val) => Math.round(val * 100),
	},
};
